/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { classNames } from '../utils/UNSAFE_classNames';
import { styles } from './themes/ButtonSetStyles.css';
import { useButtonSetContext } from '../hooks/UNSAFE_useButtonSetContext';
import { ButtonSetItems } from './ButtonSetItems';
import { useContext } from 'preact/hooks';

import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { useRovingTabIndexContainer } from '../hooks/PRIVATE_useRovingTabIndexContainer';
import { TabbableModeContext, useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { mergeRefs } from '../utils/PRIVATE_refUtils';

type WidthProps = Pick<DimensionProps, 'width'>;

export type ButtonSetProps = WidthProps &
  TestIdProps & {
    /**
     * The children are an array of buttonsetitems
     */
    children?: ComponentChildren;

    /**
     * aria-label - label for accessibility
     */
    'aria-label'?: string;

    /**
     * aria-labelledby - label for accessibility
     */
    'aria-labelledby'?: string;

    /**
     * aria-controls - specifies what this buttonset control controls.
     */
    'aria-controls'?: string;

    /**
     * The ID of an element (or space separated IDs of multiple elements) that
     * describes the buttonsetsingle.
     */
    'aria-describedby'?: string;
  };

const interpolations = [...Object.values(dimensionInterpolations)];
const styleInterpolations = mergeInterpolations<DimensionProps>(interpolations);

/**
 * ButtonSet allow users to select the state of one or more related options.
 */
export const ButtonSet = forwardRef(
  (
    {
      children,
      testId,
      'aria-label': accessibleLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-controls': ariaControls,
      'aria-describedby': ariaDescribedBy,
      width
    }: ButtonSetProps,
    ref?: Ref<HTMLSpanElement>
  ) => {
    const { layoutWidth } = useButtonSetContext();
    const testIdProps = useTestId(testId);

    const { isTabbable } = useTabbableMode();
    const { isRoving } = useContext(TabbableModeContext);

    const {
      focusedId,
      rovingTabIndexContainerProps: { ref: rovingRef, ...rovingProps }
    } = useRovingTabIndexContainer(!isTabbable);

    const { class: cls, ...intStyles } = styleInterpolations({ width });
    const rootStyles = classNames([
      styles.base,
      cls,
      layoutWidth === 'equal' ? styles.fullWidth : undefined
    ]);

    return (
      <span
        role="toolbar"
        class={rootStyles}
        style={intStyles}
        ref={isRoving ? ref : mergeRefs(ref, rovingRef)}
        aria-label={accessibleLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-controls={ariaControls}
        {...testIdProps}
        {...rovingProps}>
        {isRoving ? (
          <ButtonSetItems>{children}</ButtonSetItems>
        ) : (
          <TabbableModeContext.Provider
            value={{ isTabbable: isTabbable, isRoving: true, focusedId: focusedId }}>
            <ButtonSetItems>{children}</ButtonSetItems>
          </TabbableModeContext.Provider>
        )}
      </span>
    );
  }
);

ButtonSet.displayName = 'ButtonSet';
